import { motion } from "framer-motion";
import { CreditCard, Banknote, Building2 } from "lucide-react";

const paymentMethods = [
  {
    imgSrc: "/images/logosPago/MercadoPago.png",
    name: "Mercado Pago",
    description: "Paga de forma segura con tu cuenta",
    bgColor: "bg-gradient-to-br from-[#009EE3] to-[#2DABE2]"
  },
  {
    imgSrc: "/images/logosPago/MercadoLibre.png",
    name: "Mercado Libre",
    description: "Visita nuestra tienda en Mercado Libre",
    bgColor: "bg-white border-2 border-[#FFE600]",
    textColor: "text-gray-800",
    descriptionColor: "text-gray-600",
    link: "https://listado.mercadolibre.com.mx/_CustId_2283121582?item_id=MLM2474328991&category_id=MLM372040&seller_id=2283121582&client=recoview-selleritems&recos_listing=true#origin=vip&component=sellerData&typeSeller=classic"
  },
  {
    logo: <Banknote className="w-12 h-12 text-white" />,
    name: "Efectivo",
    description: "Pago en efectivo al recibir",
    bgColor: "bg-gradient-to-br from-[#10B981] to-[#059669]"
  },
  {
    logo: <Building2 className="w-12 h-12 text-white" />,
    name: "Depósito",
    description: "Transferencia o depósito bancario",
    bgColor: "bg-gradient-to-br from-[#6366F1] to-[#4F46E5]"
  },
  {
    logo: (
      <div className="flex gap-2">
        <svg viewBox="0 0 48 32" className="w-10 h-7">
          <rect width="48" height="32" rx="4" fill="#1A1F71" />
          <ellipse cx="18" cy="16" rx="9" ry="9" fill="#EB001B" />
          <ellipse cx="30" cy="16" rx="9" ry="9" fill="#FF5F00" />
          <path d="M24 9c-2.5 2-4 5-4 7s1.5 5 4 7c2.5-2 4-5 4-7s-1.5-5-4-7z" fill="#F79E1B" />
        </svg>
        <svg viewBox="0 0 48 32" className="w-10 h-7">
          <rect width="48" height="32" rx="4" fill="#00579F" />
          <rect x="8" y="10" width="12" height="12" fill="#F7B600" />
          <rect x="20" y="10" width="12" height="12" fill="#F7B600" />
          <rect x="28" y="10" width="12" height="12" fill="#F7B600" />
        </svg>
      </div>
    ),
    name: "Tarjeta en Sucursal",
    description: "Visa, Mastercard, débito o crédito",
    bgColor: "bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED]"
  },
  {
    imgSrc: "/images/logosPago/spei.png",
    name: "SPEI",
    description: "Transferencia electrónica interbancaria",
    bgColor: "bg-gradient-to-br from-[#EC4899] to-[#DB2777]"
  }
];

const PaymentMethods = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white via-[#F7F4F0] to-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-96 h-96 bg-[#C6A76D] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#C6A76D] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 bg-[#C6A76D]/10 px-4 py-2 rounded-full mb-4">
            <CreditCard className="w-4 h-4 text-[#C6A76D]" />
            <span className="text-sm font-medium text-[#C6A76D]">Facilidades de Pago</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#2A2520] mb-4">
            Métodos de Pago
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Aceptamos múltiples formas de pago para tu comodidad y seguridad
          </p>
        </motion.div>

        {/* Payment Methods Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {paymentMethods.map((method, index) => {
            const content = (
              <>
                {/* Content */}
                <div className="relative z-10">
                  {/* Logo with background */}
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-xl ${method.bgColor} mb-4 shadow-md group-hover:scale-110 transition-transform duration-300 p-2`}>
                    {('imgSrc' in method && (method as any).imgSrc) ? (
                      <img
                        src={(method as any).imgSrc}
                        alt={method.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      method.logo
                    )}
                  </div>

                  {/* Text */}
                  <h3 className={`text-xl font-bold mb-2 group-hover:text-[#C6A76D] transition-colors ${
                    method.textColor || 'text-[#2A2520]'
                  }`}>
                    {method.name}
                  </h3>
                  <p className={`text-sm leading-relaxed ${
                    method.descriptionColor || 'text-gray-600'
                  }`}>
                    {method.description}
                  </p>
                </div>

                {/* Hover border effect */}
                <div className="absolute inset-0 border-2 border-[#C6A76D] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </>
            );

            if (method.link) {
              return (
                <motion.a
                  key={index}
                  href={method.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={itemVariants}
                  whileHover={{ 
                    y: -8,
                    scale: 1.03,
                    transition: { duration: 0.3 }
                  }}
                  className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden block"
                >
                  {content}
                </motion.a>
              );
            }

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ 
                  y: -8,
                  scale: 1.03,
                  transition: { duration: 0.3 }
                }}
                className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
              >
                {content}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Additional Info */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 px-6 py-3 rounded-full">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <p className="text-sm font-medium text-green-700">
              Todos los pagos son 100% seguros y verificados
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PaymentMethods;
